import React, { useState, useEffect, useMemo } from 'react';
import DataTable from './DataTable';
import GeneNetworkGraph from './GeneNetworkGraph';
import MultiVariantNetwork from './MultiVariantNetwork';
import BurdenChart from './BurdenChart';
import './ResultsModal.css';

const ResultsModal = ({ isOpen, onClose, data }) => {
  const [activeTab, setActiveTab] = useState('');
  const [activeSubTable, setActiveSubTable] = useState('');
  const [analysisType, setAnalysisType] = useState('');
  const [organizedData, setOrganizedData] = useState({});
  const [uploadInfo, setUploadInfo] = useState({});
  const [isClosing, setIsClosing] = useState(false);

  // Determine analysis type and organize data
  useEffect(() => {
    if (!isOpen || !data || !data.files) {
      return;
    }

    const fileNames = data.files.map(f => f.name);
    
    // Determine analysis type based on files present
    if (fileNames.some(name => name.includes('gene_output'))) {
      setAnalysisType('Gene');
      organizeGeneData(data.files);
      setActiveTab('Gene Evidence Summary');
    } else if (fileNames.some(name => name.includes('singlevariant'))) {
      setAnalysisType('SingleVariant');
      organizeSingleVariantData(data.files);
      setActiveTab('Variant Summary');
    } else if (fileNames.some(name => name.includes('multivariant'))) {
      setAnalysisType('MultiVariant');
      organizeMultiVariantData(data.files);
    } else if (fileNames.some(name => name.includes('singlesv'))) {
      setAnalysisType('SingleSV');
      organizeSingleSVData(data.files);
      setActiveTab('SingleSV Analysis');
    } else if (fileNames.some(name => name.includes('multisv'))) {
      setAnalysisType('MultiSV');
      organizeMultiSVData(data.files);
      setActiveTab('Analysis');
    } else {
      setAnalysisType('Unknown');
      setOrganizedData({});
    }
    
    // Set upload info
    const uploadDate = formatDate(data.upload_date);
    const fileInfo = getFileTypeInfo(data);
    setUploadInfo({ uploadDate, fileInfo });
  }, [isOpen, data]);

  // Helper function to determine file type icon and class - copied from Home.js
  const getFileTypeInfo = (item) => {
    const filename = item.original_files?.toLowerCase() || '';
    
    if (filename.includes('vcf') || filename.includes('.vcf.gz')) {
      return { icon: 'fa-file-medical-alt', cssClass: 'vcf', label: 'VCF File' };
    } else if (filename.includes('chr') && 
             (filename.includes('_del') || 
              filename.includes('_dup') || 
              filename.match(/\d+_[atcg]_[atcg]/i))) {
      return { icon: 'fa-dna', cssClass: 'variant', label: 'Variant' };
    } else if (filename.match(/^[a-z0-9]+$/i) || filename.includes('gene')) {
      return { icon: 'fa-dna', cssClass: 'gene', label: 'Gene' };
    } else {
      return { icon: 'fa-file-alt', cssClass: '', label: 'Analysis' };
    }
  };
  
  // Format date for display - copied from Home.js
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const organizeGeneData = (files) => {
    const organized = {
      'Gene Evidence Summary': {
        tables: [
          {
            name: 'Clinical Evidence (ClinGen)',
            data: getFileContent(files, 'gene_output.clingen.txt'),
            active: true
          },
          {
            name: 'Clinical Evidence (ClinVar)',
            data: getFileContent(files, 'gene_output.clinvar.txt'),
            active: false
          },
          {
            name: 'Gene-Disease (GenCC)',
            data: getFileContent(files, 'gene_output.genecc.txt'),
            active: false
          }
        ]
      },
      'Expression Levels Analysis': {
        tables: [
          {
            name: 'Expression Analysis',
            data: getFileContent(files, 'gene_output.expression.txt'),
            active: true
          }
        ]
      },
      'Network Analysis': {
        tables: [
          {
            name: 'Network Interactions',
            data: getFileContent(files, 'gene_output.network.txt'),
            active: true
          }
        ]
      },
      'XQTL and Gene-Drug Interactions': {
        tables: [
          {
            name: 'eQTL Catalog',
            data: extractXQTLSubTable(getFileContent(files, 'gene_output.xqtl.txt'), 'eQTL_Catalog'),
            active: true
          },
          {
            name: 'eQTL GTEx',
            data: extractXQTLSubTable(getFileContent(files, 'gene_output.xqtl.txt'), 'eQTL_GTEx'),
            active: false
          },
          {
            name: 'pQTL',
            data: extractXQTLSubTable(getFileContent(files, 'gene_output.xqtl.txt'), 'pQTL'),
            active: false
          },
          {
            name: 'Gene-Drug',
            data: getFileContent(files, 'gene_output.drug.txt'),
            active: false
          }
        ]
      }
    };

    setOrganizedData(organized);
    setActiveSubTable('Clinical Evidence (ClinGen)');
  };

  const organizeSingleVariantData = (files) => {
    const organized = {
      'Variant Summary': {
        tables: [
          {
            name: 'Variant Summary',
            data: getFileContent(files, 'singlevariant_nodup.txt'),
            active: true
          }
        ]
      },
      'GWAS, Traits, PGx': {
        tables: []
      },

    };

    // Split GWAS, Traits, PGx by Source column
    const gwasFile = files.find(f => f.name.includes('singlevariant_output.gwas_trait_pgx.txt'));
    if (gwasFile && gwasFile.data) {
      const lines = gwasFile.data.trim().split('\n');
      if (lines.length > 1) {
        const headers = lines[0];
        const dataLines = lines.slice(1);
        
        // Find Source column
        const headerArray = headers.split('\t');
        const sourceIndex = headerArray.findIndex(h => h.toLowerCase().includes('source'));
        
        if (sourceIndex !== -1) {
          const sourceGroups = {};
          
          // Group by Source
          dataLines.forEach(line => {
            const cells = line.split('\t');
            const source = cells[sourceIndex] || 'Unknown';
            if (!sourceGroups[source]) {
              sourceGroups[source] = [];
            }
            sourceGroups[source].push(line);
          });
          
          // Create tables for each source
          Object.keys(sourceGroups).forEach((source, index) => {
            if (sourceGroups[source].length > 0) {
              const sourceData = headers + '\n' + sourceGroups[source].join('\n');
              const sourceNames = {
                'GO-BP': 'GO Biological Process',
                'GO-CC': 'GO Cellular Component', 
                'GO-MF': 'GO Molecular Function',
                'KEGG': 'KEGG Pathways'
              };
              
              organized['GWAS, Traits, PGx'].tables.push({
                name: sourceNames[source] || source,
                data: sourceData,
                active: index === 0
              });
            }
          });
        }
      }
    }

    // Add xQTL data
    const xqtlFile = files.find(f => f.name.includes('singlevariant_output.xqtl.txt'));
    if (xqtlFile && xqtlFile.data) {
      organized['GWAS, Traits, PGx'].tables.push({
        name: 'xQTL',
        data: xqtlFile.data,
        active: organized['GWAS, Traits, PGx'].tables.length === 0
      });
    }

    setOrganizedData(organized);
    setActiveSubTable('Variant Summary');
  };

  const organizeMultiVariantData = (files) => {
    const organized = {};

    // 1. Variant Summary Tab - includes basic info + burden charts
    const basicInfoFile = files.find(f => f.name.includes('multivariant_output.basicinfo.txt'));
    const diseaseBurdenFile = files.find(f => f.name.includes('multivariant_output.disease_burden_bar.txt'));
    const geneBurdenFile = files.find(f => f.name.includes('multivariant_output.gene_burden_bar.txt'));
    
    if (basicInfoFile || diseaseBurdenFile || geneBurdenFile) {
      const variantSummaryData = {
        tables: [],
        charts: []
      };
      
      // Basic information table
      if (basicInfoFile) {
        variantSummaryData.tables.push({
          name: 'Basic Information',
          data: basicInfoFile.data,
          active: true
        });
      }
      
      // Disease burden chart data
      if (diseaseBurdenFile) {
        variantSummaryData.charts.push({
          name: 'Disease Burden',
          data: diseaseBurdenFile.data,
          type: 'burden-bar',
          active: true
        });
      }
      
      // Gene burden chart data
      if (geneBurdenFile) {
        variantSummaryData.charts.push({
          name: 'Gene Burden',
          data: geneBurdenFile.data,
          type: 'burden-bar',
          active: variantSummaryData.charts.length === 0
        });
      }
      
      organized['Variant Summary'] = variantSummaryData;
    }

    // 2. Enrichment Analysis Tab - split by Source column
    const enrichmentFile = files.find(f => f.name.includes('multivariant_output.enrichment.txt'));
    if (enrichmentFile && enrichmentFile.data) {
      const lines = enrichmentFile.data.trim().split('\n');
      if (lines.length > 1) {
        const headers = lines[0];
        const dataLines = lines.slice(1);
        
        // Find Source column index
        const headerCols = headers.split('\t');
        const sourceIndex = headerCols.indexOf('Source');
        
        if (sourceIndex !== -1) {
          // Group by Source
          const sourceGroups = {
            'GO-BP': [],
            'GO-CC': [],
            'GO-MF': [], 
            'KEGG': []
          };
          
          dataLines.forEach(line => {
            const cols = line.split('\t');
            const source = cols[sourceIndex]?.trim();
            if (sourceGroups[source]) {
              sourceGroups[source].push(line);
            }
          });
          
          // Create tables for each source group
          const enrichmentTables = [];
          Object.entries(sourceGroups).forEach(([source, rows]) => {
            if (rows.length > 0) {
              const sourceData = headers + '\n' + rows.join('\n');
              const sourceNames = {
                'GO-BP': 'GO Biological Process',
                'GO-CC': 'GO Cellular Component', 
                'GO-MF': 'GO Molecular Function',
                'KEGG': 'KEGG Pathways'
              };
              
              enrichmentTables.push({
                name: sourceNames[source] || source,
                data: sourceData,
                active: enrichmentTables.length === 0
              });
            }
          });
          
          if (enrichmentTables.length > 0) {
            organized['Enrichment Analysis'] = {
              tables: enrichmentTables
            };
          }
        }
      }
    }

    // 3. Network Analysis Tab
    const networkFile = files.find(f => f.name.includes('multivariant_output.network.txt'));
    if (networkFile) {
      organized['Network Analysis'] = {
        tables: [
          {
            name: 'Gene-Variant Network',
            data: networkFile.data,
            active: true
          }
        ]
      };
    }

    // 4. GWAS, Traits, PGx Tab
    const gwasFile = files.find(f => f.name.includes('multivariant_output.gwas.txt'));
    const traitFile = files.find(f => f.name.includes('multivariant_output.trait.txt'));
    const pgxFile = files.find(f => f.name.includes('multivariant_output.pgx.txt'));
    
    if (gwasFile || traitFile || pgxFile) {
      const gwasTraitsTables = [];
      
      if (gwasFile) {
        gwasTraitsTables.push({
          name: 'GWAS',
          data: gwasFile.data,
          active: true
        });
      }
      
      if (traitFile) {
        gwasTraitsTables.push({
          name: 'Traits',
          data: traitFile.data,
          active: gwasTraitsTables.length === 0
        });
      }
      
      if (pgxFile) {
        gwasTraitsTables.push({
          name: 'PGx',
          data: pgxFile.data,
          active: gwasTraitsTables.length === 0
        });
      }
      
      organized['GWAS, Traits, PGx'] = {
        tables: gwasTraitsTables
      };
    }

    setOrganizedData(organized);
    
    // Set initial active tab
    const firstTab = Object.keys(organized)[0];
    if (firstTab) {
      setActiveTab(firstTab);
      const firstTable = organized[firstTab]?.tables?.[0];
      if (firstTable) {
        setActiveSubTable(firstTable.name);
      }
    }
  };

  const organizeSingleSVData = (files) => {
    const organized = {
      'SingleSV Analysis': {
        tables: [
          {
            name: 'Structural Variants',
            data: getFileContent(files, 'singlesv_nodup.tsv'),
            active: true
          }
        ]
      }
    };

    setOrganizedData(organized);
    setActiveSubTable('Structural Variants');
  };

  const organizeMultiSVData = (files) => {
    const tables = [];
    
    // Variant Summary table
    const variantFile = files.find(f => f.name.includes('multisv_nodup.tsv'));
    if (variantFile) {
      tables.push({
        name: 'Variant Summary',
        data: variantFile.data,
        active: tables.length === 0
      });
    }
    
    // Enrichment Analysis - split by Source column
    const enrichmentFile = files.find(f => f.name.includes('multisv_output.enrichment.txt'));
    if (enrichmentFile && enrichmentFile.data) {
      const lines = enrichmentFile.data.trim().split('\n');
      if (lines.length > 1) {
        const headers = lines[0];
        const dataLines = lines.slice(1);
        
        // Find Source column index
        const headerCols = headers.split('\t');
        const sourceIndex = headerCols.indexOf('Source');
        
        if (sourceIndex !== -1) {
          // Group by Source
          const sourceGroups = {
            'GO-BP': [],
            'GO-CC': [],
            'GO-MF': [], 
            'KEGG': []
          };
          
          dataLines.forEach(line => {
            const cols = line.split('\t');
            const source = cols[sourceIndex]?.trim();
            if (sourceGroups[source]) {
              sourceGroups[source].push(line);
            }
          });
          
          // Create tables for each source group
          Object.entries(sourceGroups).forEach(([source, rows]) => {
            if (rows.length > 0) {
              const sourceData = headers + '\n' + rows.join('\n');
              const sourceNames = {
                'GO-BP': 'GO Biological Process',
                'GO-CC': 'GO Cellular Component', 
                'GO-MF': 'GO Molecular Function',
                'KEGG': 'KEGG Pathways'
              };
              
              tables.push({
                name: sourceNames[source] || source,
                data: sourceData,
                active: tables.length === 0
              });
            }
          });
        }
      }
    }
    
    // Get PNG files for display
    const pngFiles = files.filter(f => f.name.includes('.png'));
    
    const organized = {
      'Analysis': {
        tables: tables,
        images: pngFiles
      }
    };

    setOrganizedData(organized);
    setActiveSubTable(tables.length > 0 ? tables[0].name : '');
  };

  const getFileContent = (files, fileName) => {
    const file = files.find(f => f.name === fileName);
    return file ? file.data : '';
  };

  const extractXQTLSubTable = (xqtlData, prefix) => {
    if (!xqtlData) return '';
    
    const lines = xqtlData.trim().split('\n');
    if (lines.length === 0) return '';
    
    const headers = lines[0].split('\t');
    const relevantHeaders = headers.filter(h => h.startsWith(prefix));
    
    if (relevantHeaders.length === 0) return '';
    
    const relevantIndices = relevantHeaders.map(h => headers.indexOf(h));
    const newHeaders = relevantHeaders.map(h => h.replace(prefix + '.', ''));
    
    const newLines = [newHeaders.join('\t')];
    
    for (let i = 1; i < lines.length; i++) {
      const cells = lines[i].split('\t');
      const newCells = relevantIndices.map(idx => cells[idx] || '');
      newLines.push(newCells.join('\t'));
    }
    
    return newLines.join('\n');
  };

  const getCurrentTabData = useMemo(() => {
    if (!organizedData[activeTab]) return null;
    return organizedData[activeTab];
  }, [organizedData, activeTab]);

  const getCurrentTableData = useMemo(() => {
    if (!getCurrentTabData) return '';
    const activeTable = getCurrentTabData.tables.find(t => t.name === activeSubTable);
    return activeTable ? activeTable.data : '';
  }, [getCurrentTabData, activeSubTable]);

  const handleTabSwitch = (tabName) => {
    setActiveTab(tabName);
    if (organizedData[tabName] && organizedData[tabName].tables.length > 0) {
      const firstTable = organizedData[tabName].tables[0];
      setActiveSubTable(firstTable.name);
    }
  };

  const handleTableSwitch = (table) => {
    setActiveSubTable(table.name);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const tabNames = Object.keys(organizedData);

  return (
    <div className={`WSNG-modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className="WSNG-modal" onClick={(e) => e.stopPropagation()}>
        <div className="WSNG-modal-header">
          <h2>{analysisType} Analysis Results</h2>
          <button className="WSNG-close-button" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        
        <div className="WSNG-modal-content">
          <div className="WSNG-analysis-container">
            {/* Upload Info and Tabs Sidebar */}
            <div className="WSNG-left-sidebar">
              {/* Upload Info Header */}
              <div className="WSNG-upload-info-header">
                <div className="WSNG-upload-info-card">
                  <div className="WSNG-info-icon">
                    <i className={`fas ${getFileTypeInfo(data).icon}`}></i>
                  </div>
                  <div className="WSNG-info-details">
                    <div className="WSNG-info-title">{data.original_files}</div>
                    {data.notes && <div className="WSNG-info-notes">{data.notes}</div>}
                    <div className="WSNG-info-meta">
                      <span className="WSNG-info-uuid">ID: {data.uuid?.substring(0, 8)}</span>
                      <span className="WSNG-info-files">{data.files?.length} files</span>
                      <span className="WSNG-info-date">{formatDate(data.upload_date)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <div className="WSNG-tab-navigation">
                {tabNames.map((tabName) => (
                  <button
                    key={tabName}
                    className={`WSNG-tab-btn ${activeTab === tabName ? 'active' : ''}`}
                    onClick={() => {
                      setActiveTab(tabName);
                      const firstTable = organizedData[tabName]?.tables?.[0];
                      if (firstTable) {
                        setActiveSubTable(firstTable.name);
                      }
                    }}
                  >
                    {tabName}
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="WSNG-content-area">
              {activeTab && organizedData[activeTab] ? (
                <div className="WSNG-table-content">
                  {/* MultiSV Two-Column Layout */}
                  {analysisType === 'MultiSV' && organizedData[activeTab].images?.length > 0 ? (
                    <div className="WSNG-multisv-layout">
                      {/* Left Column - Plots */}
                      <div className="WSNG-multisv-plots">
                        <div className="WSNG-images-section stacked">
                          <h4>GO Enrichment Plots</h4>
                          <div className="WSNG-images-grid">
                            {organizedData[activeTab].images.map((image, index) => (
                              <div key={index} className="WSNG-image-card">
                                <img src={`data:image/png;base64,${image.data}`} alt={image.name} />
                                <span className="WSNG-image-title">{image.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                      
                      {/* Right Column - Table */}
                      <div className="WSNG-multisv-table">
                        {organizedData[activeTab].tables?.length > 0 ? (
                          <DataTable
                            data={organizedData[activeTab].tables.find(t => t.name === activeSubTable)?.data || ''}
                            title={activeSubTable}
                            availableTables={organizedData[activeTab].tables.map(table => ({
                              ...table,
                              active: table.name === activeSubTable
                            }))}
                            onTableSwitch={(table) => setActiveSubTable(table.name)}
                          />
                        ) : (
                          <div className="WSNG-no-analysis">
                            No analysis data available for this category.
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* Standard Layout for other analysis types */
                    <>
                      {/* Special handling for MultiVariant Network Analysis */}
                      {analysisType === 'MultiVariant' && activeTab === 'Network Analysis' ? (
                        <MultiVariantNetwork 
                          data={organizedData[activeTab].tables.find(t => t.name === activeSubTable)?.data || ''}
                        />
                      ) : /* Special handling for Gene Network Analysis */
                      analysisType === 'Gene' && activeTab === 'Network Analysis' ? (
                        <GeneNetworkGraph 
                          data={organizedData[activeTab].tables.find(t => t.name === activeSubTable)?.data || ''}
                        />
                      ) : /* Special handling for MultiVariant Variant Summary with burden charts */
                      analysisType === 'MultiVariant' && activeTab === 'Variant Summary' ? (
                        <div className="WSNG-variant-summary-layout">
                          {/* Left Column - Burden Charts */}
                          <div className="WSNG-burden-charts-column">
                            {organizedData[activeTab].charts?.length > 0 && (
                              <div className="WSNG-burden-charts-section">
                                <h3>Burden Analysis</h3>
                                <div className="WSNG-burden-charts-stack">
                                  {organizedData[activeTab].charts.map((chart, index) => (
                                    <BurdenChart
                                      key={index}
                                      data={chart.data}
                                      title={chart.name}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          {/* Right Column - Basic Info Table */}
                          <div className="WSNG-variant-summary-table">
                            {organizedData[activeTab].tables?.length > 0 && (
                              <DataTable
                                data={organizedData[activeTab].tables.find(t => t.name === activeSubTable)?.data || ''}
                                title={activeSubTable}
                                availableTables={organizedData[activeTab].tables.map(table => ({
                                  ...table,
                                  active: table.name === activeSubTable
                                }))}
                                onTableSwitch={(table) => setActiveSubTable(table.name)}
                              />
                            )}
                          </div>
                        </div>
                      ) : organizedData[activeTab].tables?.length > 0 ? (
                        <DataTable
                          data={organizedData[activeTab].tables.find(t => t.name === activeSubTable)?.data || ''}
                          title={activeSubTable}
                          availableTables={organizedData[activeTab].tables.map(table => ({
                            ...table,
                            active: table.name === activeSubTable
                          }))}
                          onTableSwitch={(table) => setActiveSubTable(table.name)}
                        />
                      ) : (
                        <div className="WSNG-no-analysis">
                          No analysis data available for this category.
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="WSNG-no-analysis">
                  Select a category to view analysis results.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsModal;
